/**
 * オーナー自身のログインから、ストアのアクターに切り替える（BOARD A-33、2026-10-21）
 *
 * 切り替えると、ストアのアクターのトークン（POST /api/auth/switch-to-store、1日有効。operatorActorId 入り）でアプリを使う。
 * - 切り替え前のトークンなどは sessionStorage に取っておく（タブを閉じると消える。共有の端末で、あとから来た人が
 *   前の人に戻れないように。以前は localStorage に残していた）
 * - 「ストアとして操作中」かどうかは、今のトークンに operatorActorId があるかで決める（取っておいた値の有無では決めない）
 * - 「自分に戻る」は、取っておいたトークンの actorId が、今のトークンの operatorActorId と一致するときだけ戻す。
 *   一致しなければ取っておいた値を消し、ログインし直してもらう
 * - ログアウト・401・新しいログインのときは clearStoreActing() で消す
 */
const KEYS = ['token', 'authToken', 'actorId', 'user'];
const SAVED_PREFIX = 'storeActing:';
const STORE_NAME_KEY = 'storeActing:storeName';

function decodeTokenPayload(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(payload), (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    // 形式が違うトークンは「切り替え中ではない」として扱う
    return null;
  }
}

/** 今のトークンが「ストアとして操作中」なら、その情報（なければ null） */
export function getStoreActing() {
  const token = localStorage.getItem('token');
  const payload = token ? decodeTokenPayload(token) : null;
  if (!payload?.operatorActorId) return null;
  return {
    operatorActorId: payload.operatorActorId,
    storeName: sessionStorage.getItem(STORE_NAME_KEY) || payload.displayName || 'ストア',
  };
}

export function isActingAsStore() {
  return Boolean(getStoreActing());
}

export async function startActingAsStore(store, apiUrl) {
  const currentToken = localStorage.getItem('token');
  const response = await fetch(`${apiUrl}/api/auth/switch-to-store`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${currentToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ storeId: store.store_id || store.id }),
  });
  const body = await response.json();
  if (!response.ok || !body.token) {
    throw new Error(body.message || 'ストアに切り替えられませんでした');
  }

  clearStoreActing();
  for (const key of KEYS) {
    const value = localStorage.getItem(key);
    if (value !== null) sessionStorage.setItem(`${SAVED_PREFIX}${key}`, value);
  }
  sessionStorage.setItem(STORE_NAME_KEY, body.store?.name || store.store_name || store.name || 'ストア');
  localStorage.setItem('token', body.token);
  localStorage.setItem('authToken', body.token);
  localStorage.setItem('actorId', String(body.actor.actor_id));
  localStorage.removeItem('user');
}

/**
 * 切り替え前に戻す。
 * @returns 戻せたら true。戻せなかったら false（ログアウトした状態にするので、呼び出し側はログイン画面へ）
 */
export function stopActingAsStore() {
  const acting = getStoreActing();
  const savedToken = sessionStorage.getItem(`${SAVED_PREFIX}token`);
  const savedPayload = savedToken ? decodeTokenPayload(savedToken) : null;
  const canReturn =
    acting && savedPayload && String(savedPayload.actorId) === String(acting.operatorActorId);

  if (canReturn) {
    for (const key of KEYS) {
      const saved = sessionStorage.getItem(`${SAVED_PREFIX}${key}`);
      if (saved !== null) {
        localStorage.setItem(key, saved);
      } else {
        localStorage.removeItem(key);
      }
    }
  } else {
    for (const key of KEYS) localStorage.removeItem(key);
  }
  clearStoreActing();
  return Boolean(canReturn);
}

export function clearStoreActing() {
  for (const key of KEYS) sessionStorage.removeItem(`${SAVED_PREFIX}${key}`);
  sessionStorage.removeItem(STORE_NAME_KEY);
  // 以前は localStorage に取っておいていたので、残っていれば消す
  for (const key of KEYS) localStorage.removeItem(`${SAVED_PREFIX}${key}`);
  localStorage.removeItem(STORE_NAME_KEY);
}
